// Phase-A R1.3 — Organizational attribution. Reused in Footer
// (PUBLIC), AppShell (WORKSPACE + AUTH_FLOW thin bar), and AboutPage.
//
// S-REBRAND-EMI موج ۳ — هویت از `brand.config` می‌آید، نه از رشتهٔ درجا.
// نگاشتِ نشان هم عوض شد: پیش‌تر `is-light` به فایلِ *سفید* وصل بود و چون تمِ
// تیره پین‌شده-خاموش است، همان سفید در نوارِ روشنِ ورک‌اسپیس و صفحهٔ «دربارهٔ
// ما» نامرئی می‌شد. حالا معنا صریح است: `is-light` = سطحِ روشن (نشانِ سرمه‌ای)
// و `is-dark` = سطحِ تیره (نشانِ سفید)؛ فوترِ عمومی با اسکوپِ `.footer` در
// styles.css نسخهٔ تیره را می‌گیرد، مستقل از تم.
//
// S-I18N-LANDING-REST — این بلوک در فوترِ *همهٔ* صفحاتِ عمومی و در نوارِ
// ورک‌اسپیس رندر می‌شود، پس متنش از namespaceِ بوتِ `shell` می‌آید نه از
// `landing`ِ تنبل. نامِ دارندهٔ حق (`RIGHTS_HOLDER_FA`) عمداً ترجمه نمی‌شود و
// فقط به‌عنوان پارامترِ `{holder}` تزریق می‌شود: نامِ حقوقیِ ثبتی است و
// ترجمه‌اش تصمیمِ مالک است، نه تصمیمِ مترجم.
import React from "react";
import { useTranslation } from "react-i18next";

import { BRAND, RIGHTS_HOLDER_FA, brandNameForLocale } from "../brand/brand.config";
import { useLocale } from "../i18n/LocaleProvider";

export type OrgAttributionVariant = "full" | "compact";

export interface OrgAttributionProps {
  /**
   * full    — long-form copyright + logo block. Used in the public
   *           marketing footer and the About page org section.
   * compact — single-line attribution row. Used as the workspace and
   *           auth-flow footnote bar.
   */
  variant?: OrgAttributionVariant;
  className?: string;
}

export const OrgAttribution: React.FC<OrgAttributionProps> = ({
  variant = "full",
  className,
}) => {
  const { t } = useTranslation("shell");
  const { locale } = useLocale();
  const orgName = brandNameForLocale(locale);
  const year = t("brand.copyrightYear");

  if (variant === "compact") {
    return (
      <div className={"org-attribution-compact " + (className ?? "")}>
        {/* S-LP-6 — هر دو <img> همیشه در DOM اند (سوییچ با display) و قبلاً هر
            دو دانلود می‌شدند؛ loading="lazy" اجازه می‌دهد مرورگر نسخهٔ مخفی را
            نگیرد. */}
        <img
          src={BRAND.assets.markOnLight96}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="org-attribution-logo is-light"
        />
        <img
          src={BRAND.assets.markOnDark96}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="org-attribution-logo is-dark"
        />
        {/* نامِ نوارِ کوتاه = **دارندهٔ حق**، نه بهره‌بردار (تصمیمِ مالک
            ۱۴۰۵/۰۵/۱۵). نشانِ سازمان کنارش می‌ماند چون این نوار هویتِ
            سکو را هم نشان می‌دهد، ولی جملهٔ «حقوق محفوظ است» باید نامِ
            دارندهٔ واقعیِ حق را بدهد. */}
        <span>{t("footer.org.rightsCompact", { year, holder: RIGHTS_HOLDER_FA })}</span>
      </div>
    );
  }
  return (
    <div className={"org-attribution-full " + (className ?? "")}>
      <div className="org-attribution-mark">
        <img
          src={BRAND.assets.markOnLight192}
          alt={orgName}
          loading="lazy"
          decoding="async"
          className="org-attribution-logo is-light"
        />
        <img
          src={BRAND.assets.markOnDark192}
          alt={orgName}
          loading="lazy"
          decoding="async"
          className="org-attribution-logo is-dark"
        />
      </div>
      <div className="org-attribution-text">
        <div className="org-attribution-name">{orgName}</div>
        <div className="org-attribution-platform">{t("brand.platformKind")}</div>
        {/* تا امروز این جمله مالکیت را به سازمان می‌داد؛ مالک تصحیح کرد که
            حقِ مادی و معنوی با مجریِ فنی است. نام از `RIGHTS_HOLDER_FA`
            می‌آید تا با نوارِ کوتاه و فوترِ عمومی هم‌صدا بماند. */}
        <div className="org-attribution-copyright">
          {t("footer.org.rightsFull", { year, holder: RIGHTS_HOLDER_FA })}
        </div>
        <div className="org-attribution-dev">
          <img
            src={BRAND.developer.mark}
            alt={BRAND.developer.nameFa}
            loading="lazy"
            decoding="async"
            className="org-attribution-dev-mark"
          />
          <span>{t("footer.org.developerLegal")}</span>
        </div>
      </div>
    </div>
  );
};

export default OrgAttribution;
